/**
 * Error tracking utilities
 * Centralizes error handling and reporting
 */

// Define error severity levels
export enum ErrorSeverity {
  CRITICAL = 'critical',
  ERROR = 'error',
  WARNING = 'warning',
  INFO = 'info',
}

// Error statistics storage
interface ErrorStats {
  errorCount: number;
  criticalCount: number;
  warningCount: number;
  lastError: Date | null;
  errors: TrackedError[];
}

// Tracked error structure
interface TrackedError {
  id: string;
  message: string;
  stack?: string;
  componentName: string;
  timestamp: Date;
  severity: ErrorSeverity;
  context?: Record<string, any>;
  resolved: boolean;
}

// Initialize error statistics
const errorStats: ErrorStats = {
  errorCount: 0,
  criticalCount: 0,
  warningCount: 0,
  lastError: null,
  errors: []
};

// Maximum number of errors to store
const MAX_STORED_ERRORS = 100;

/**
 * Track an error with metadata
 */
export const trackError = (
  error: Error,
  componentName: string,
  context?: Record<string, any>,
  severity: ErrorSeverity = ErrorSeverity.ERROR
): void => {
  // Increment error counts
  errorStats.errorCount++;
  if (severity === ErrorSeverity.CRITICAL) {
    errorStats.criticalCount++;
  } else if (severity === ErrorSeverity.WARNING) {
    errorStats.warningCount++;
  }
  
  // Update last error timestamp
  errorStats.lastError = new Date();
  
  // Create error object with unique ID
  const trackedError: TrackedError = {
    id: `err_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
    message: error.message,
    stack: error.stack,
    componentName,
    timestamp: new Date(),
    severity,
    context,
    resolved: false
  };
  
  // Add to error list, keeping only the most recent errors
  errorStats.errors.unshift(trackedError);
  if (errorStats.errors.length > MAX_STORED_ERRORS) {
    errorStats.errors.pop();
  }
  
  // Log to console in development
  if (process.env.NODE_ENV === 'development') {
    console.error(
      `[${severity.toUpperCase()}][${componentName}] ${error.message}`, 
      { context, timestamp: trackedError.timestamp }
    );
  }
  
  // In production, you would send to an error tracking service
  if (process.env.NODE_ENV === 'production') {
    // Implement real error reporting service integration here
    try {
      // Example: reportToErrorService(trackedError);
      console.error(`[${severity}] Error in ${componentName}: ${error.message}`);
    } catch (e) {
      // Silent fail for error reporter failures
      console.error('Failed to report error to service:', e);
    }
  }
  
  // Store in localStorage for recovery purposes
  try {
    localStorage.setItem('lastError', JSON.stringify({
      message: error.message,
      component: componentName,
      timestamp: trackedError.timestamp,
      severity
    }));
  } catch (e) {
    // Silent fail for localStorage issues
  }
};

/**
 * Mark an error as resolved
 */
export const resolveError = (errorId: string): boolean => {
  const error = errorStats.errors.find(err => err.id === errorId);
  
  if (error) {
    error.resolved = true;
    return true;
  }
  
  return false;
};

/**
 * Get current error statistics
 */
export const getErrorStats = (): ErrorStats => {
  return { ...errorStats };
};

/**
 * Clear error history
 */
export const clearErrorStats = (): void => {
  errorStats.errorCount = 0;
  errorStats.criticalCount = 0;
  errorStats.warningCount = 0;
  errorStats.lastError = null;
  errorStats.errors = [];
};

/**
 * Get unresolved errors
 */
export const getUnresolvedErrors = (): TrackedError[] => {
  return errorStats.errors.filter(err => !err.resolved);
};

/**
 * Get recent errors (resolved and unresolved)
 */
export const getRecentErrors = (limit = 10): TrackedError[] => {
  return errorStats.errors.slice(0, limit);
};

export default {
  trackError,
  resolveError,
  getErrorStats,
  clearErrorStats,
  getUnresolvedErrors,
  getRecentErrors
};
